/**
 * Transfer (Internal Movement) Workflow Simulator
 * Simulates the complete warehouse transfer workflow using real API calls
 */

import { getSharedRateLimiter } from '../services/RateLimiter';
import { VirtualOperator } from '../services/VirtualOperator';
import { BatchSerialHelper } from '../services/BatchSerialHelper';
import { AuthService } from '../../../../../services/authService';
import { SimulatorConfig } from '../types';

// API base URL - use environment variable for production deployments
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Authenticated fetch helper using AuthService
const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  return AuthService.authenticatedFetch(url, options);
};

export interface TransferWorkflowResult {
  success: boolean;
  batchId?: string;
  transfersCompleted: number;
  quantityMoved: number;
  error?: string;
  duration: number;
}

export class TransferWorkflow {
  private config: SimulatorConfig;
  private operator: VirtualOperator;
  private rateLimiter = getSharedRateLimiter();

  constructor(config: SimulatorConfig, operator: VirtualOperator) {
    this.config = config;
    this.operator = operator;
  }

  /**
   * Execute one complete transfer workflow cycle
   */
  async execute(): Promise<TransferWorkflowResult> {
    const startTime = Date.now();
    let batchId: string | undefined;
    let transfersCompleted = 0;
    let quantityMoved = 0;

    try {
      // Step 1: Create a new transfer session
      this.operator.startTask('transfer', 'Oppretter overføringssession');
      await this.rateLimiter.throttle();

      // Use operator ID + milliseconds to ensure unique BatchID across multiple simulators
      batchId = `SIM-XFER-${this.operator.getId()}-${Date.now()}`;

      await this.createTransferSession(batchId);

      // Step 2: Get item locations for transfers
      this.operator.startTask('transfer', 'Henter lokasjoner for overføring');
      await this.rateLimiter.throttle();

      const itemLocations = await this.getItemLocationsForTransfer();

      if (itemLocations.length < 2) {
        // Need at least 2 locations to transfer between
        await this.completeTransferSession(batchId);
        return {
          success: true,
          batchId,
          transfersCompleted: 0,
          quantityMoved: 0,
          duration: Date.now() - startTime,
          error: 'Ikke nok lokasjoner for overføring'
        };
      }

      // Step 3: Select items to transfer (max 3 for simulation)
      const itemsToTransfer = itemLocations
        .filter((loc: any) => loc.availableQuantity > 0)
        .slice(0, 3);

      // Step 4: Execute transfers
      const batchSerialEnabled = this.config.batchSerialConfig?.enabled ?? false;
      const batchHelper = batchSerialEnabled ? new BatchSerialHelper() : null;

      for (const item of itemsToTransfer) {
        // Find a different location to transfer to
        const targetLocation = itemLocations.find(
          (loc: any) => loc.locationName !== item.locationName
        );

        if (!targetLocation) continue;

        this.operator.startTask(
          'transfer',
          `Overfører ${item.itemNo} fra ${item.locationName} til ${targetLocation.locationName}`
        );

        // Simulate walking to source location
        await this.delay(this.config.timing.walkTimeMs);

        // Simulate picking up items
        await this.delay(this.config.timing.pickTimeMs);

        // Simulate walking to target location
        await this.delay(this.config.timing.walkTimeMs);

        // Simulate placing items
        await this.delay(this.config.timing.placeTimeMs);

        // Calculate transfer quantity (transfer 50% of available)
        const transferQty = Math.max(1, Math.floor(item.availableQuantity / 2));

        // Fetch batch/serial info from source location if enabled
        let batchSerialFields: Record<string, string> = {};
        if (batchSerialEnabled && batchHelper) {
          const itemType = item.itemType ?? 0;
          if (BatchSerialHelper.needsBatchTracking(itemType)) {
            const batches = await batchHelper.getBatchesAtBin(item.itemNo, this.config.warehouseId, item.locationName);
            if (batches.length > 0) {
              batchSerialFields = {
                itemBatchNo: batches[0].batchNo,
                ...(batches[0].expiryDate ? { itemBatchExpDate: batches[0].expiryDate } : {})
              };
            }
          } else if (BatchSerialHelper.needsSerialTracking(itemType)) {
            const serials = await batchHelper.getSerialNumbersAtBin(item.itemNo, this.config.warehouseId, item.locationName);
            if (serials.length > 0) {
              batchSerialFields = { itemSerialNo: serials[0].serialNo };
            }
          }
        }

        // Add transfer transaction
        await this.rateLimiter.throttle();
        await this.addTransferTransaction(batchId, {
          itemNo: item.itemNo,
          fromLocationName: item.locationName,
          toLocationName: targetLocation.locationName,
          quantity: transferQty,
          fromWarehouse: item.warehouseID || this.config.warehouseId,
          toWarehouse: targetLocation.warehouseID || this.config.warehouseId,
          ...batchSerialFields
        });

        transfersCompleted++;
        quantityMoved += transferQty;
      }

      // Step 5: Complete the transfer session
      this.operator.startTask('transfer', `Fullfører overføringssession ${batchId}`);
      await this.rateLimiter.throttle();
      await this.completeTransferSession(batchId);

      return {
        success: true,
        batchId,
        transfersCompleted,
        quantityMoved,
        duration: Date.now() - startTime
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ukjent feil';
      console.error(`[TransferWorkflow] Error:`, errorMessage);

      return {
        success: false,
        batchId,
        transfersCompleted,
        quantityMoved,
        duration: Date.now() - startTime,
        error: errorMessage
      };
    }
  }

  /**
   * Create a new transfer session
   */
  private async createTransferSession(batchId: string): Promise<any> {
    const now = new Date();

    const response = await authenticatedFetch(
      `${API_BASE_URL}/stocktrans`,
      {
        method: 'POST',
        body: JSON.stringify({
          batchID: batchId,
          operatorID: this.operator.getId(),
          orderDate: now.toISOString(),
          description1: `Simulator overføring ${now.toLocaleDateString('no-NO')} ${now.toLocaleTimeString('no-NO')}`,
          orderStatus: 20, // Under arbeid
          orderType: 2, // Transfer
          warehouse: this.config.warehouseId,
          startWorkDateTime: now.toISOString()
        })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to create transfer session: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get item locations for transfer
   */
  private async getItemLocationsForTransfer(): Promise<any[]> {
    try {
      // Get all item bins and filter by warehouse
      const response = await authenticatedFetch(
        `${API_BASE_URL}/data-inspection/itembins`
      );

      if (response.ok) {
        const allBins = await response.json();
        // Filter to bins in this warehouse with transferable quantities
        // Map API fields to expected format
        return allBins
          .filter((b: any) =>
            (b.quantity > 0 || b.quantityAvailable > 0) &&
            (b.warehouseID === this.config.warehouseId || b.warehouse === this.config.warehouseId)
          )
          .map((b: any) => ({
            itemNo: b.itemNo,
            locationName: b.binCode || b.locationName,
            warehouseID: b.warehouseID || b.warehouse,
            availableQuantity: b.quantityAvailable || b.quantity || 0,
            quantity: b.quantity || 0
          }));
      }
    } catch (error) {
      console.warn('[TransferWorkflow] Could not get item locations:', error);
    }

    return [];
  }

  /**
   * Add a transfer transaction
   */
  private async addTransferTransaction(batchId: string, data: {
    itemNo: string;
    fromLocationName: string;
    toLocationName: string;
    quantity: number;
    fromWarehouse: string;
    toWarehouse: string;
    itemBatchNo?: string;
    itemBatchExpDate?: string;
    itemSerialNo?: string;
  }): Promise<any> {
    const encodedBatchId = encodeURIComponent(batchId);

    const body: Record<string, any> = {
      batchID: batchId,
      itemNo: data.itemNo,
      locationName: data.fromLocationName,
      toLocationName: data.toLocationName,
      quantity: data.quantity,
      warehouse: data.fromWarehouse,
      toWarehouse: data.toWarehouse,
      operatorID: this.operator.getId(),
      lineType: 2, // Transfer
      transType: 11, // TransType 11 = Lageroverføring (for transaction history)
      lineStatus: 0,
      transDt: new Date().toISOString()
    };

    if (data.itemBatchNo) body.itemBatchNo = data.itemBatchNo;
    if (data.itemBatchExpDate) body.itemBatchExpDate = data.itemBatchExpDate;
    if (data.itemSerialNo) body.itemSerialNo = data.itemSerialNo;

    const response = await authenticatedFetch(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/transactions`,
      {
        method: 'POST',
        body: JSON.stringify(body)
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to add transfer transaction: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Complete the transfer session
   */
  private async completeTransferSession(batchId: string): Promise<void> {
    const encodedBatchId = encodeURIComponent(batchId);

    const response = await authenticatedFetch(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/complete`,
      {
        method: 'POST',
        body: JSON.stringify({})
      }
    );

    if (!response.ok) {
      // Non-critical - session may still be valid
      console.warn(`[TransferWorkflow] Could not complete transfer session: ${response.statusText}`);
    }
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default TransferWorkflow;
